import type { LensCardData, LensCardView, LensDataset, LensValueFormat } from '~/types/leLens';

import {
  type LensRange,
  type LensSelection,
  applyRanges,
  applySelection,
  applyView,
  missingViewMeasures,
} from '../cardView';
import { applyRowFields, prepareFields } from '../datasetFields';
import { decimalsForTwoSignificantFigures, formatLensValue } from '../lensChart';

import { type MetricHeadline, resolveMetricHeadline } from './metricValue';

/** Both periods must evaluate the same view, including calculated fields and ratios. */
export function resolveViewedMetric(
  data: Pick<LensCardData, 'rows' | 'columns' | 'aggregated'>,
  view: LensCardView | undefined,
  fields: LensDataset['fields'],
  selection?: LensSelection,
  ranges?: Record<string, LensRange>,
): MetricHeadline {
  const prepared = prepareFields(fields, data.columns ?? []);
  const columns = [...(data.columns ?? []), ...prepared.row.map((entry) => entry.field.name)];
  const missing = missingViewMeasures(view, columns, prepared.aggregate);
  if (missing.length) {
    return { kind: 'refused', reason: `The result does not contain ${missing.join(' and ')}.` };
  }
  const rows = applyRowFields(data.rows ?? [], prepared.row);
  const shaped = applyView(
    applyRanges(applySelection(rows, columns, selection), columns, ranges),
    columns,
    view,
    'metric',
    prepared.aggregate,
    data.aggregated,
  );
  return resolveMetricHeadline(shaped.rows, shaped.columns);
}

export function formatMetricMovement(current: number, previous: number, format?: LensValueFormat): string | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) {
    return null;
  }
  const delta = current - previous;
  const sign = delta >= 0 ? '+' : '−';
  if (format?.kind === 'percent') {
    const points = Math.abs(delta) * (format.percentScale === 'unit' ? 100 : 1);
    return `${sign}${formatLensValue(points, { kind: 'number', decimals: format.decimals ?? 1, locale: format.locale })}pp`;
  }
  const absolute = `${sign}${formatLensValue(Math.abs(delta), format)}`;
  if (previous === 0) {
    return absolute;
  }
  const percentage = Math.abs((delta / Math.abs(previous)) * 100);
  return `${sign}${twoSignificantFigures(percentage)}% (${absolute})`;
}

/** Two significant figures, never fewer than the whole part: 0.42, 3.1, 16, 123. */
function twoSignificantFigures(value: number): string {
  return value.toFixed(decimalsForTwoSignificantFigures(value));
}

/** Compact labels keep the KPI delta to a short second line. */
export function compactMetricComparisonLabel(label: string): string {
  const normalized = label.trim().toLowerCase();
  if (normalized === 'previous period') return 'PP';
  if (normalized === 'previous year') return 'PY';
  return label.trim();
}

/** The sign remains in the copy, so colour reinforces rather than carries meaning alone. */
export function metricMovementTone(current: number, previous: number): 'positive' | 'negative' | 'neutral' {
  if (current > previous) return 'positive';
  if (current < previous) return 'negative';
  return 'neutral';
}
